import { useState, useContext, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../componentes/Plantilla/Plantilla';
import { DataContext } from '../../contexto/ContextoDatos';
import CarruselRelacionados from '../../componentes/Catalogo/CarruselRelacionados';
import ContactSection from '../../componentes/Secciones/Contacto/SeccionContacto';
import GaleriaProducto from '../../componentes/Secciones/Productos/GaleriaProducto';
import Breadcrumbs from '../../componentes/UI/Breadcrumbs';
import styles from './PaginaProductos.module.css';

const TABS = [
  { id: 'general',      label: 'Visión General' },
  { id: 'specs',        label: 'Especificaciones' },
  { id: 'certs',        label: 'Certificaciones' },
  { id: 'docs',         label: 'Documentación' },
];

const WaveBottom = () => (
  <svg className={styles.detailWave} viewBox="0 0 1440 56" preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0,0 C480,56 960,56 1440,0 L1440,56 L0,56 Z" fill="var(--bg-light, #f8fafc)" />
  </svg>
);

const CertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00b4d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const cleanPdfUrl = (raw) => {
  if (!raw) return null;
  return raw.replace(/^chrome-extension:\/\/[^/]+\//, '');
};

export const ProductDetail = () => {
  const { productSlug } = useParams();
  const { products } = useContext(DataContext);
  const [activeTab, setActiveTab] = useState('general');

  const product = products.find(p => p.slug === productSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab('general');
  }, [productSlug]);

  if (!product) {
    return (
      <Layout>
        <div className="container" style={{ padding: '140px 24px', textAlign: 'center' }}>
          <h2>Producto no encontrado</h2>
          <p>El producto solicitado no existe o ha sido modificado.</p>
          <Link to="/productos" className="btn btn-primary" style={{ marginTop: '20px' }}>
            Ver todos los productos
          </Link>
        </div>
      </Layout>
    );
  }

  const hasDocs = product.docs && product.docs.length > 0;
  const certs = product.filters?.specialties?.filter(s => s !== 'Equipo Estándar') || [];
  const hasCerts = certs.length > 0;

  return (
    <Layout>
      {/* ── Banner ─────────────────────────────────────────────────────────── */}
      <div className={styles.detailBanner}>
        <div className="container">
          <div className={styles.detailBannerInner}>
            <div className={styles.detailBannerContent}>
              <Breadcrumbs 
                paths={[
                  { label: 'Inicio', to: '/' },
                  { label: 'Productos', to: '/productos' },
                  { label: product.category, to: `/productos?categorias=${product.category_slug}` },
                  { label: product.name }
                ]} 
              />

              <div className={styles.detailBadges}>
                {product.best_seller && (
                  <span className={styles.detailBadgeBestSeller}>BEST SELLER</span>
                )}
                {product.on_demand && (
                  <span className={styles.detailBadgeBajoPedido}>BAJO PEDIDO</span>
                )}
              </div>

              <h1 className={styles.detailTitle}>{product.name}</h1>

              <dl className={styles.detailMeta}>
                {product.brand && (
                  <>
                    <dt>Marca</dt>
                    <dd>{product.brand}</dd>
                  </>
                )}
                {product.model && (
                  <>
                    <dt>Modelo</dt>
                    <dd>{product.model}</dd>
                  </>
                )}
                <dt>Categoría</dt>
                <dd>{product.category}</dd>
              </dl>
            </div>

            <div className={styles.detailBannerImageArea}>
              <GaleriaProducto slug={product.slug} productName={product.name} />
            </div>
          </div>
        </div>
        <WaveBottom />
      </div>

      {/* ── Tab navigation ─────────────────────────────────────────────────── */}
      <div className={styles.tabsWrapper}>
        <div className="container">
          <nav className={styles.tabNav} role="tablist">
            {TABS.filter(tab => {
              if (tab.id === 'docs' && !hasDocs) return false;
              if (tab.id === 'certs' && !hasCerts) return false;
              return true;
            }).map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`${styles.tabNavBtn} ${activeTab === tab.id ? styles.tabNavBtnActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* ── Tab content ────────────────────────────────────────────────────── */}
      <section className={styles.tabPanel}>
        <div className="container">

          {activeTab === 'general' && (
            <div className={styles.panelGeneral}>
              {product.use && (
                <p className={styles.panelUse}>{product.use}</p>
              )}
              {product.description && (
                <p className={styles.panelDescription}>{product.description}</p>
              )}
              <a href="#contact" className="btn btn-primary" style={{ marginTop: '32px', display: 'inline-block' }}>
                Solicitar cotización
              </a>
            </div>
          )}

          {activeTab === 'specs' && (
            <div className={styles.panelSpecs}>
              <h2 className={styles.panelHeading}>Especificaciones Técnicas</h2>
              {product.specs_table ? (
                <div className={styles.customTableWrapper}>
                  <table className={styles.customTable}>
                    <thead>
                      <tr>
                        {product.specs_table.headers.map((h, i) => (
                          <th key={i} className={styles.customTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {product.specs_table.rows.map((row, i) => (
                        <tr key={i} className={styles.customTr}>
                          {row.map((cell, j) => (
                            <td key={j} className={styles.customTd}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <>
                  <dl className={styles.specGrid}>
                    {product.brand && (
                      <div className={styles.specRow}>
                        <dt className={styles.specKey}>Marca</dt>
                        <dd className={styles.specVal}>{product.brand}</dd>
                      </div>
                    )}
                    {product.model && (
                      <div className={styles.specRow}>
                        <dt className={styles.specKey}>Modelo</dt>
                        <dd className={styles.specVal}>{product.model}</dd>
                      </div>
                    )}
                    {product.type && (
                      <div className={styles.specRow}>
                        <dt className={styles.specKey}>Tipo</dt>
                        <dd className={styles.specVal}>{product.type}</dd>
                      </div>
                    )}
                    {product.use && (
                      <div className={styles.specRow}>
                        <dt className={styles.specKey}>Aplicación</dt>
                        <dd className={styles.specVal}>{product.use}</dd>
                      </div>
                    )}
                    <div className={styles.specRow}>
                      <dt className={styles.specKey}>Categoría</dt>
                      <dd className={styles.specVal}>{product.category}</dd>
                    </div>
                  </dl>
                </>
              )}
            </div>
          )}

          {activeTab === 'certs' && (
            <div className={styles.panelCerts}>
              <h2 className={styles.panelHeading}>Certificaciones y Especialidad</h2>
              <div className={styles.docsGrid}>
                {certs.map((cert, idx) => {
                  let desc = '';
                  if (cert === 'Certificación ATEX') {
                    desc = 'Equipos diseñados y certificados para operar de forma segura en atmósferas con riesgo de explosión, previniendo fuentes de ignición en entornos industriales críticos.';
                  } else if (cert === 'Grado Sanitario') {
                    desc = 'Construcción bajo estrictos estándares de higiene (acero inoxidable, filtros especializados), ideal para la industria alimentaria, farmacéutica y barreras sanitarias.';
                  } else if (cert === 'Sistemas Eco / Recuperación') {
                    desc = 'Incorpora tecnología avanzada para la máxima eficiencia energética y/o sistemas innovadores de recuperación de agua, promoviendo la sostenibilidad.';
                  }
                  
                  return (
                    <div key={idx} className={styles.docCard} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                      <div style={{ flexShrink: 0, marginTop: '2px' }}>
                        <CertIcon />
                      </div>
                      <div>
                        <div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--text-dark, #0f172a)' }}>{cert}</div>
                        {desc && <div style={{ fontSize: '0.95rem', color: 'var(--text-muted, #64748b)', marginTop: '4px', lineHeight: '1.5' }}>{desc}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'docs' && (
            <div className={styles.panelDocs}>
              <h2 className={styles.panelHeading}>Documentación Oficial</h2>
              <div className={styles.docsGrid}>
                {product.docs.map((doc, idx) => (
                  <div key={idx} className={styles.docCard}>
                    <p className={styles.docCardTitle}>{doc.title || `Documento ${idx + 1}`}</p>
                    <p className={styles.docCardDesc}>
                      Visualiza o descarga la documentación técnica oficial proporcionada por el fabricante.
                    </p>
                    <div className={styles.docActionButtons}>
                      <a
                        href={cleanPdfUrl(doc.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`btn btn-primary ${styles.btnDocPrimary}`}
                      >
                        Ver Documento
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}



        </div>
      </section>

      {/* ── Related carousel ───────────────────────────────────────────────── */}
      <CarruselRelacionados
        currentSlug={product.slug}
        categorySlug={product.category_slug}
        products={products}
      />

      {/* ── Contact ────────────────────────────────────────────────────────── */}
      <ContactSection prefillMessage={`Hola SEIMA, me interesa solicitar una cotización sobre ${product.name}${product.model ? ` (Modelo: ${product.model})` : ''}.`} />
    </Layout>
  );
};
export default ProductDetail;
